import React, {
  AnchorHTMLAttributes,
  ClassAttributes,
  createElement,
  HTMLAttributes,
  InputHTMLAttributes,
  PureComponent,
} from 'react'

import ReactMarkdown from 'react-markdown'
import gfm from 'remark-gfm'
import GithubSlugger from 'github-slugger'
import CheckboxRadio from '@components/CheckboxRadio/CheckboxRadio'

import { importAllImages } from '@helpers/import'

import './Markdown.scss'

interface CodeProps {
  node?: any
  inline?: any
  className?: any
  children?: any
}

type RendererHeading = ClassAttributes<HTMLHeadingElement> & HTMLAttributes<HTMLHeadingElement> & CodeProps
type RendererLink = ClassAttributes<HTMLAnchorElement> & AnchorHTMLAttributes<HTMLAnchorElement> & CodeProps
type RendererInput = ClassAttributes<HTMLInputElement> & InputHTMLAttributes<HTMLInputElement> & CodeProps

// We import every images that could be needed by the Markdown component
const imagesFiles = importAllImages()

interface OwnProps {
  content: string
  className?: string
  isArialFontFamily?: boolean
}

type Props = OwnProps

export class Markdown extends PureComponent<Props> {
  private slugger = new GithubSlugger()

  transformImageUri = (url: string): string => {
    const imageKey = url.split('/').pop()
    if (imageKey) {
      return imagesFiles[imageKey] ?? url
    }

    return url
  }

  renderHeading = (props: RendererHeading) => {
    const {
      children,
      node: { properties, tagName },
    } = props
    const { slugger } = this
    const [firstChild] = children
    let innerText: undefined | string = undefined
    if (typeof firstChild === 'string') {
      innerText = firstChild
    }
    return createElement(
      tagName,
      {
        id: innerText ? slugger.slug(innerText) : undefined,
        ...properties,
      },
      children,
    )
  }

  renderLink = ({ href, children, node: { properties } }: RendererLink) => {
    const isAbsolute = (url: string): boolean => /^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(url)
    const absoluteHref = href && !href.startsWith('mailto:') && isAbsolute(href)
    return createElement(
      'a',
      {
        href,
        target: absoluteHref ? '_blank' : undefined,
        rel: absoluteHref ? 'noreferrer' : undefined,
        ...properties,
      },
      children,
    )
  }

  renderCheckbox = ({ checked, disabled, node: { properties }, type }: RendererInput) => {
    return (
      <CheckboxRadio
        {...properties}
        type={type as 'checkbox' | 'radio' | undefined}
        value={checked}
        disabled={disabled}
      />
    )
  }

  // Go to "/markdown" page to see how to use
  render() {
    const { props, renderLink, renderHeading, renderCheckbox } = this
    const { content = '', className: receivedClassName = '', isArialFontFamily = false } = props
    const className = `Markdown ${receivedClassName}`

    const renderers = {
      a: renderLink,
      h1: renderHeading,
      h2: renderHeading,
      h3: renderHeading,
      h4: renderHeading,
      h5: renderHeading,
      h6: renderHeading,
      input: renderCheckbox,
    }
    return (
      <ReactMarkdown
        className={`${className} ${isArialFontFamily ? 'Markdown-Arial' : ''}`}
        remarkPlugins={[gfm]}
        components={renderers}
        urlTransform={this.transformImageUri}>
        {content}
      </ReactMarkdown>
    )
  }
}

export default Markdown
